import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatTabChangeEvent, MatTabGroup} from '@angular/material/tabs';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { Task } from 'src/app/services/dtos/task';
import { MessageState } from 'src/app/services/enums/messageStates.enum';
import { ProcessFlow } from 'src/app/services/enums/processFlow.enum';
import {
  DeleteTask,
  FetchTask,
  ScheduleTaskForReRun,
} from 'src/app/states/tasks-state/tasks.actions';
import { RightsService } from '../../../../services/rights.service';
import { TasksState } from './../../../../states/tasks-state/tasks.state';
import { ChangeLogEntry } from 'src/app/services/dtos/changeLogEntry';

@Component({
  selector: 'app-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss'],
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MatTabGroup) public matTabGroup!: MatTabGroup;

  @Output()
  public taskDeleted: EventEmitter<null> = new EventEmitter();

  @Input() public taskSubject!: Subject<Task>;

  @Select(TasksState.getSelectedTaskId)
  public selectedTaskId$!: Observable<string>;

  // Make enums available in HTML template:
  public processFlow = ProcessFlow;
  public messageState = MessageState;

  public componentTitle!: string;
  public componentSubTitle!: string;
  public selectedTask: Task = new Task();

  public taskInputContent!: string;
  public taskOutputContent!: string;
  public taskMapContent!: string;
  public taskTransformContent!: string;
  public taskAuditTrail!: Subject<ChangeLogEntry[]>;

  private taskId!: string;
  private subs = new Array<Subscription>();
  private previousTab = '';
  private previousTaskId = '';

  public constructor(
    public accessService: RightsService,
    private store: Store,
    private router: Router
  ) {}

  public get hasCanSeeCustomersRight(): boolean {
    return this.accessService.canSeeCustomers();
  }

  public get hasCanDeleteTasksRight(): boolean {
    return this.accessService.canDeleteTasks();
  }

  public get hasCanResendTaskRight(): boolean {
    return this.accessService.canResendTask();
  }

  public ngOnInit(): void {
    this.taskAuditTrail = new Subject();
    this.subs.push(
      this.selectedTaskId$.subscribe((taskId) => {
        if (taskId !== '') {
          this.taskId = taskId;
          this.store.dispatch(new FetchTask(taskId));
          this.subs.push(
            this.store.select(TasksState.getSelectedTask).subscribe((task) => {
              if (task) {
                this.componentTitle = task.customerReference;
                this.componentSubTitle = task.customerName;
                this.selectedTask = task;
                this.taskInputContent = task.inputUtf8;
                this.taskOutputContent = task.outputUtf8;
                this.taskMapContent = task.mapUtf8;
                this.taskTransformContent = task.transformUtf8;
                this.taskAuditTrail.next(task.changeLog ?? '');
              }
            })
          );
        }
      })
    );
  }

  public tabChanged(event: MatTabChangeEvent) {
    if (this.previousTaskId === this.selectedTask.id) {
      this.matTabGroup.selectedIndex = event.index;
      this.previousTab = event.tab.textLabel;
      return;
    }
    if (
      this.previousTab === 'Map' &&
      event.index === 1 &&
      this.matTabGroup.selectedIndex === 1 &&
      this.selectedTask.processFlow === this.processFlow.order
    ) {
      this.matTabGroup.selectedIndex = 2;
      return;
    } else if (
      this.previousTab === 'Transform' &&
      event.index === 2 &&
      this.matTabGroup.selectedIndex === 2 &&
      this.selectedTask.processFlow === this.processFlow.order
    ) {
      this.matTabGroup.selectedIndex = 1;
      return;
    }

    if (
      this.previousTab === 'Map' &&
      event.index === 2 &&
      this.matTabGroup.selectedIndex === 2 &&
      (this.selectedTask.processFlow === this.processFlow.result ||
        this.selectedTask.processFlow === this.processFlow.invoice)
    ) {
      this.matTabGroup.selectedIndex = 1;
      return;
    } else if (
      this.previousTab === 'Transform' &&
      event.index === 1 &&
      this.matTabGroup.selectedIndex === 1 &&
      (this.selectedTask.processFlow === this.processFlow.result ||
        this.selectedTask.processFlow === this.processFlow.invoice)
    ) {
      this.matTabGroup.selectedIndex = 2;
      return;
    }

    this.matTabGroup.selectedIndex = event.index;
    this.previousTab = event.tab.textLabel;
    this.previousTaskId = this.taskId;
  }

  public resendButtonVisible(): boolean {
    return (
      this.hasCanResendTaskRight &&
      this.selectedTask.processFlow !== this.processFlow.action
    );
  }

  public goToCustomer(): void {
    // TODO: remove duplicated check. Implement Angular route guards for this
    if (!this.hasCanSeeCustomersRight) {
      return;
    }

    if (this.selectedTask.customerIsDeleted) {
      alert('Customer deleted');
      return;
    }

    this.router.navigate(['/customers', { id: this.selectedTask.customerId }]);
  }

  public deleteTask(): void {
    const shouldDelete = confirm(
      `Are you sure you want to delete selected task?`
    );

    if (shouldDelete) {
      this.subs.push(
        this.store
          .dispatch(new DeleteTask(this.taskId))
          .subscribe(() => this.taskDeleted.emit())
      );
    }
  }

  public saveInput(): void {
    this.saveFile(this.taskInputContent, this.selectedTask.inputExtension);
  }

  public saveMap(): void {
    this.saveFile(this.taskMapContent, this.selectedTask.mapExtension);
  }

  public saveTransform(): void {
    this.saveFile(this.taskTransformContent, this.selectedTask.transformExtension);
  }

  public scheduleTaskForReRun(): void {
    this.store.dispatch(new ScheduleTaskForReRun(this.selectedTask));
  }

  public saveOutput(): void {
    this.saveFile(this.taskOutputContent, this.selectedTask.outputExtension);
  }

  public ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private saveFile(content: string, extension: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const link = document.createElement('a');
    let posDot = this.selectedTask.customerReference.lastIndexOf('.');
    if(posDot === -1) {posDot = this.selectedTask.customerReference.length;}
    const filename = this.selectedTask.customerReference.substring(0, posDot);
    link.setAttribute('href', window.URL.createObjectURL(blob));
    link.setAttribute('download',  filename + extension);
    link.click();
  }
}
